import {
  Array,
  Boolean,
  Dictionary,
  Literal,
  Null,
  Number,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { ToolNameLiteral } from "./agent/toolNames.js";
import { EffortLevel, ModelSpeedLiteral } from "./effortLevel.js";
import { ModelFeatures } from "./ModelFeatures.js";

export const AnthropicLikeProviderTypeLiteral = Union(
  Literal("ANTHROPIC"),
  Literal("BEDROCK"),
  /**
   * VERTEX = Gemini Enterprise Agent Platform (formerly Vertex AI).
   * This value gets persisted in MagicApiKey.providerName so would need a migration to updated
   */
  Literal("VERTEX"),
);
export type AnthropicLikeProviderType = Static<
  typeof AnthropicLikeProviderTypeLiteral
>;
export const AnthropicLikeProviderType = getNormalEnum(
  AnthropicLikeProviderTypeLiteral,
);

export type AnthropicProviderFeatures = {
  compaction: boolean;
  toolSearch: "new" | "legacy";
  taskBudgets: boolean;
  /**
   * Whether the provider honors the `speed: "fast"` param.
   */
  fastMode: boolean;
};

export const AnthropicProviderFeatureMap: Record<
  AnthropicLikeProviderType,
  AnthropicProviderFeatures
> = {
  [AnthropicLikeProviderType.ANTHROPIC]: {
    compaction: true,
    toolSearch: "new",
    taskBudgets: true,
    fastMode: true,
  },
  [AnthropicLikeProviderType.BEDROCK]: {
    // Bedrock supports server-side compaction via the InvokeModel API (which is
    // what @anthropic-ai/bedrock-sdk uses), using the same compact-2026-01-12
    // beta header + context_management envelope as the direct Anthropic API.
    compaction: true,
    toolSearch: "legacy",
    taskBudgets: false,
    fastMode: false,
  },
  [AnthropicLikeProviderType.VERTEX]: {
    // Vertex's Anthropic beta rollout is inconsistent per model/region (the
    // Haiku tier in particular lags), so leave compaction off until it can be
    // validated against the exact models/regions we route to.
    compaction: false,
    toolSearch: "legacy",
    taskBudgets: false,
    fastMode: false,
  },
};

/** @deprecated */
export const DeprecatedAnthropicModelLiteral = Union(
  Literal("CLAUDE_4_SONNET"),
  Literal("CLAUDE_SONNET_35"),
  Literal("CLAUDE_SONNET_35_NEW"),
  Literal("CLAUDE_SONNET_37"),
  Literal("CLAUDE_3_OPUS"),
  Literal("RESEARCH_MODEL_FIREFLY"),
  Literal("CLAUDE_4_OPUS"),
  Literal("CLAUDE_4_5_OPUS"),
  Literal("CLAUDE_NOUGAT_EAP"),
  Literal("CLAUDE_FUDGE_EAP"),
  Literal("CLAUDE_CHURRO_EAP"),
);
/** @deprecated */
export type DeprecatedAnthropicModel = Static<
  typeof DeprecatedAnthropicModelLiteral
>;
/** @deprecated */
export const DeprecatedAnthropicModel = getNormalEnum(
  DeprecatedAnthropicModelLiteral,
);

export const StableAnthropicModelLiteral = Union(
  Literal("CLAUDE_4_5_SONNET"),
  Literal("CLAUDE_4_5_HAIKU"),
  Literal("CLAUDE_4_6_OPUS"),
  Literal("CLAUDE_4_7_OPUS"),
  Literal("CLAUDE_4_8_OPUS"),
  Literal("CLAUDE_OPUS_5"),
  Literal("CLAUDE_4_6_SONNET"),
  Literal("CLAUDE_5_SONNET"),
);
export type StableAnthropicModel = Static<typeof StableAnthropicModelLiteral>;
export const StableAnthropicModel = getNormalEnum(StableAnthropicModelLiteral);

export const ExperimentalAnthropicModelLiteral = Union(
  Literal("CLAUDE_FABLE_5"),
  Literal("CLAUDE_FABLE_5_1"),
);
export type ExperimentalAnthropicModel = Static<
  typeof ExperimentalAnthropicModelLiteral
>;
export const ExperimentalAnthropicModel = getNormalEnum(
  ExperimentalAnthropicModelLiteral,
);

export const AnthropicModelLiteral = Union(
  ...StableAnthropicModelLiteral.alternatives,
  ...ExperimentalAnthropicModelLiteral.alternatives,
);
export type AnthropicModel = Static<typeof AnthropicModelLiteral>;
export const AnthropicModel = getNormalEnum(AnthropicModelLiteral);

export const GeminiEAPMultiRegionLiteral = Union(Literal("US"), Literal("EU"));
export type GeminiEAPMultiRegion = Static<typeof GeminiEAPMultiRegionLiteral>;
export const GeminiEAPMultiRegion = getNormalEnum(GeminiEAPMultiRegionLiteral);

export type AnthropicModelFeatures = ModelFeatures & {
  /**
   * Should omit geo prefixes
   * @see {@link https://docs.aws.amazon.com/bedrock/latest/userguide/model-cards.html}
   */
  bedrockName: string;
  /**
   * @see {@link https://docs.cloud.google.com/vertex-ai/generative-ai/docs/partner-models/claude}
   */
  geminiEAPName: string;
  /**
    * For each possible psuedo region, the actual region the model is supported in

    * @see {@link https://docs.cloud.google.com/gemini-enterprise-agent-platform/resources/locations}
    */
  geminiEAPRegion: Record<GeminiEAPMultiRegion, string>;
  oneMillionContext?: true;
  /** Whether manual extended thinking supports interleaved thinking via its beta header. */
  interleavedThinking?: true;
  adaptiveThinking?: true;
  summarizedAdaptiveThinking?: true;
  omitsSamplingParams?: true;
  xHighEffort?: true;
  compaction?: true;
  effort?: true;
  serverSideFallbackModel?: AnthropicModel;
  /** Whether the model accepts task budgets in `output_config` via its beta header. */
  taskBudgets?: true;
  /** Whether the model supports `speed: "fast"` via the fast-mode beta header. */
  fastMode?: true;
};

export const AnthropicModelFeatureMap: Record<
  AnthropicModel,
  AnthropicModelFeatures
> = {
  [AnthropicModel.CLAUDE_4_5_SONNET]: {
    name: "claude-sonnet-4-5-20250929",
    label: "Sonnet 4.5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-sonnet-4-5-20250929-v1:0",
    geminiEAPName: "claude-sonnet-4-5@20250929",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "europe-west1",
      [GeminiEAPMultiRegion.US]: "us-east5",
    },
    interleavedThinking: true,
  },
  [AnthropicModel.CLAUDE_4_5_HAIKU]: {
    name: "claude-haiku-4-5-20251001",
    label: "Haiku 4.5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-haiku-4-5-20251001-v1:0",
    geminiEAPName: "claude-haiku-4-5@20251001",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "europe-west1",
      [GeminiEAPMultiRegion.US]: "us-east5",
    },
  },
  [AnthropicModel.CLAUDE_4_6_OPUS]: {
    name: "claude-opus-4-6",
    label: "Opus 4.6",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-4-6-v1",
    geminiEAPName: "claude-opus-4-6",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "europe-west1",
      [GeminiEAPMultiRegion.US]: "us-east5",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
  },
  [AnthropicModel.CLAUDE_4_7_OPUS]: {
    name: "claude-opus-4-7",
    label: "Opus 4.7",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-4-7",
    geminiEAPName: "claude-opus-4-7",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
  },
  [AnthropicModel.CLAUDE_4_8_OPUS]: {
    name: "claude-opus-4-8",
    label: "Opus 4.8",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-4-8",
    geminiEAPName: "claude-opus-4-8",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
    fastMode: true,
  },
  [AnthropicModel.CLAUDE_OPUS_5]: {
    name: "claude-opus-5",
    label: "Opus 5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-opus-5",
    geminiEAPName: "claude-opus-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
    fastMode: true,
  },
  [AnthropicModel.CLAUDE_FABLE_5]: {
    name: "claude-fable-5",
    label: "Fable 5",
    requiresDataRetention: true,
    bedrockName: "anthropic.claude-fable-5",
    geminiEAPName: "claude-fable-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    serverSideFallbackModel: AnthropicModel.CLAUDE_4_8_OPUS,
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
    taskBudgets: true,
  },
  [AnthropicModel.CLAUDE_FABLE_5_1]: {
    name: "claude-fable-5-1",
    label: "Fable 5.1",
    requiresDataRetention: true,
    bedrockName: "anthropic.claude-fable-5-1",
    geminiEAPName: "claude-fable-5-1",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
  },
  [AnthropicModel.CLAUDE_4_6_SONNET]: {
    name: "claude-sonnet-4-6",
    label: "Sonnet 4.6",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-sonnet-4-6",
    geminiEAPName: "claude-sonnet-4-6",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "europe-west1",
      [GeminiEAPMultiRegion.US]: "us-east5",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
  },
  [AnthropicModel.CLAUDE_5_SONNET]: {
    name: "claude-sonnet-5",
    label: "Sonnet 5",
    requiresDataRetention: false,
    bedrockName: "anthropic.claude-sonnet-5",
    geminiEAPName: "claude-sonnet-5",
    geminiEAPRegion: {
      [GeminiEAPMultiRegion.EU]: "eu",
      [GeminiEAPMultiRegion.US]: "us",
    },
    effort: true,
    compaction: true,
    adaptiveThinking: true,
    oneMillionContext: true,
    omitsSamplingParams: true,
    summarizedAdaptiveThinking: true,
    xHighEffort: true,
  },
};

/**
 * Resolves the Bedrock model ID for a region by adding a cross-region
 * inference-profile geo prefix so the model works regardless of the specific
 * region the credentials were created in. Falls back to `global` for
 * non-us/eu regions.
 * @see {@link https://docs.aws.amazon.com/bedrock/latest/userguide/models-region-compatibility.html}
 */
export function getBedrockModelName({
  awsRegion,
  model,
}: {
  model: AnthropicModel;
  awsRegion: string;
}): string {
  const baseName = AnthropicModelFeatureMap[model].bedrockName;
  const prefix = awsRegion.startsWith("us-")
    ? "us"
    : awsRegion.startsWith("eu-")
      ? "eu"
      : "global";
  return `${prefix}.${baseName}`;
}

/**
 * Resolves the Gemini EAP region to use for a model. When a `multiRegion` is
 * provided, returns the model's multi-region endpoint from
 * `AnthropicModelFeatureMap`. Otherwise, maps a specific `region` to the
 * corresponding multi-region endpoint (`europe-*` -> EU, `us-*` -> US), falling
 * back to the region as-is for anything else.
 * @see {@link https://docs.cloud.google.com/gemini-enterprise-agent-platform/resources/locations}
 */
export function getGeminiEAPRegion({
  model,
  multiRegion,
  region,
}: {
  model: AnthropicModel;
  region?: string;
  multiRegion?: GeminiEAPMultiRegion;
}): string {
  if (multiRegion != null) {
    return AnthropicModelFeatureMap[model].geminiEAPRegion[multiRegion];
  } else if (region != null) {
    if (region.startsWith("europe-")) {
      return getGeminiEAPRegion({
        model,
        multiRegion: GeminiEAPMultiRegion.EU,
      });
    } else if (region.startsWith("us-")) {
      return getGeminiEAPRegion({
        model,
        multiRegion: GeminiEAPMultiRegion.US,
      });
    } else {
      return region;
    }
  } else {
    throw new Error("Cannot resolve gemini EAP region");
  }
}

export const AnthropicToolChoiceLiteral = Union(
  // Literal("tool"), // TODO(@zkirby): Support "tool" tool choice option.
  Literal("any"),
  Literal("auto"),
  Literal("none"),
);
export type AnthropicToolChoice = Static<typeof AnthropicToolChoiceLiteral>;

export const DEFAULT_THINKING_BUDGET_TOKENS = 5000;
// The minimum allowed while still "being on" is 1024
// https://docs.aws.amazon.com/bedrock/latest/userguide/claude-messages-extended-thinking.html
export const DEFAULT_SMALL_THINKING_BUDGET_TOKENS = 1024;
export const DEFAULT_MAX_TOKENS = 20000;

const CacheControl = RRecord({
  type: Literal("ephemeral"),
});

export const AnthropicThinkingBlock = RRecord({
  type: Literal("thinking"),
  thinking: String,
  signature: String,
  cache_control: CacheControl.optional(),
});
export type AnthropicThinkingBlock = Static<typeof AnthropicThinkingBlock>;

export const RedactedAnthropicThinkingBlock = RRecord({
  type: Literal("redacted_thinking"),
  data: String, // field is 'data' not 'thinking' for redacted blocks
  signature: String,
  cache_control: CacheControl.optional(),
});
export type RedactedAnthropicThinkingBlock = Static<
  typeof RedactedAnthropicThinkingBlock
>;

export const AnthropicThinkingBlocks = Array(
  Union(AnthropicThinkingBlock, RedactedAnthropicThinkingBlock),
);
export type AnthropicThinkingBlocks = Static<typeof AnthropicThinkingBlocks>;

export const AnthropicThinkingConfig = RRecord({
  enabled: Boolean,
  budgetTokens: Number,
});
export type AnthropicThinkingConfig = Static<typeof AnthropicThinkingConfig>;

export const AnthropicTaskBudget = RRecord({
  type: Literal("tokens"),
  total: Number,
  remaining: Number.optional(),
});
export type AnthropicTaskBudget = Static<typeof AnthropicTaskBudget>;

export const AnthropicEffortLiteral = Union(
  Literal("low"),
  Literal("medium"),
  Literal("high"),
  Literal("xhigh"),
  Literal("max"),
);
export type AnthropicEffort = Static<typeof AnthropicEffortLiteral>;

/**
 * Canonical {@link EffortLevel} → Anthropic API param. `none` and `minimal`
 * collapse to `low`; per-model clamping (e.g. xhigh→high) happens downstream
 * in `buildEffortForRequest`.
 */
export function toAnthropic(p: EffortLevel): AnthropicEffort {
  if (p === "none" || p === "minimal") return "low";
  return p;
}

/**
 * Config for Anthropic's tool search feature (beta header: advanced-tool-use-20
 * Each agent defines its own always-loaded tools in *_DEFAULT_ALWAYS_LOADED_TOOL_NAMES.
 * If tool search is enabled, other tools are deferred and loaded on demand.
 */
export const AnthropicToolSearchConfig = RRecord({
  enabled: Boolean,
  /** Tool names that should never be deferred (always loaded into context) */
  alwaysLoadedTools: Array(ToolNameLiteral).optional(),
});
export type AnthropicToolSearchConfig = Static<
  typeof AnthropicToolSearchConfig
>;

/**
 * Shared guidance describing _what_ a compaction summary should contain,
 * independent of the output mechanism (raw text/tags vs. structured output).
 *
 * These instructions optimize for "resume immediately" continuity in long
 * agent loops while avoiding excess detail that can be re-fetched.
 */
export const COMPACTION_SUMMARY_GUIDANCE = `
You have written a partial transcript for the initial task above. Please write a summary of the transcript.

The purpose of this summary is continuity: a future run may not have access to the raw history, and must continue the same task using only your summary without repeating already completed work or forgetting both long term goals, immediate next steps, or currently in progress work.

Write down anything that would be helpful, including the state, next steps, learnings etc. including but not limited to:
- The user's current goal, constraints, preferences, and success criteria.
- What has been completed, what remains, and the exact current state.
- Key decisions, assumptions, tradeoffs, and rationale already established.
- Important failures, pitfalls, and "do not repeat" learnings.
- Any blockers or open questions that still need user input.
- Immediate and detailed spec-level next steps in priority order.

Keep it concise and action-oriented:
- Preserve stable, human-meaningful references (cell names/purposes, dataset/table names, feature names).
- Do not include ephemeral IDs (short cell refs, UUIDs, tool call IDs). These will be automatically re-fetched from the notebook and can be referenced here in context with plain english.
- Do not dump large raw outputs or full code/SQL unless required for correctness, these can all be re-fetched from the notebook later ad hoc.

End with:
- "Next Steps" checklist (ordered by priority, concrete actions).
- "Critical Reminders" (short bullets of must-not-forget constraints/pitfalls).
`.trim();

/**
 * Default instructions for Anthropic server-side compaction summaries.
 *
 * Used by the native Anthropic context-management compaction feature, which
 * returns the summary as free text wrapped in a <summary> block.
 */
export const DEFAULT_ANTHROPIC_COMPACTION_INSTRUCTIONS = `${COMPACTION_SUMMARY_GUIDANCE}

You must wrap your summary in a <summary></summary> block and output nothing else.`;

/**
 * Config for Anthropic server-side compaction context management edits.
 */
export const AnthropicCompactionConfig = RRecord({
  enabled: Boolean,
  trigger: RRecord({
    type: Literal("input_tokens"),
    value: Number,
  }).optional(),
  pauseAfterCompaction: Boolean.optional(),
  instructions: String.optional(),
});
export type AnthropicCompactionConfig = Static<
  typeof AnthropicCompactionConfig
>;
export const AnthropicDefaultCompactionConfig: AnthropicCompactionConfig = {
  enabled: true,
  pauseAfterCompaction: false,
  instructions: DEFAULT_ANTHROPIC_COMPACTION_INSTRUCTIONS,
  trigger: {
    type: "input_tokens",
    value: 300000,
  },
};

export const AnthropicModelParams = RRecord({
  model: AnthropicModelLiteral,
  maxTokens: Number,
  stopSequences: Array(String),
  temperature: Number.optional(),
  topK: Number.optional(),
  topP: Number.optional(),
  thinking: AnthropicThinkingConfig.optional(),
  effort: AnthropicEffortLiteral.optional(),
  speed: ModelSpeedLiteral.optional(),
  taskBudget: AnthropicTaskBudget.optional(),
  toolSearch: AnthropicToolSearchConfig.optional(),
});
export type AnthropicModelParams = Static<typeof AnthropicModelParams>;

export const AnthropicMessageRoleLiteral = Union(
  Literal("user"),
  Literal("assistant"),
);
export type AnthropicMessageRole = Static<typeof AnthropicMessageRoleLiteral>;
export const AnthropicMessageRole = getNormalEnum(AnthropicMessageRoleLiteral);

export const TextBlock = RRecord({
  type: Literal("text"),
  text: String,
  cache_control: CacheControl.optional(),
});
export type TextBlock = Static<typeof TextBlock>;

export const AnthropicMediaType = Union(
  Literal("image/png"),
  Literal("image/jpeg"),
  Literal("image/webp"),
  Literal("image/gif"),
);
type AnthropicMediaType = Static<typeof AnthropicMediaType>;

const ImageBlock = RRecord({
  type: Literal("image"),
  source: RRecord({
    type: Literal("base64"),
    media_type: AnthropicMediaType,
    data: String,
  }),
  cache_control: CacheControl.optional(),
});
export type ImageBlock = Static<typeof ImageBlock>;

const DocumentBlock = RRecord({
  type: Literal("document"),
  source: RRecord({
    type: Literal("base64"),
    media_type: Literal("application/pdf"),
    data: String,
  }),
  cache_control: CacheControl.optional(),
});
export type DocumentBlock = Static<typeof DocumentBlock>;

export const ToolUseBlock = RRecord({
  type: Literal("tool_use"),
  id: String,
  name: String,
  input: Dictionary(Unknown, String),
  thinking: String.optional(),
  cache_control: CacheControl.optional(),
});
export type ToolUseBlock = Static<typeof ToolUseBlock>;

/**
 * Tool reference block for client-side tool search.
 * Used in tool_result content to reference deferred tools.
 *
 * @see https://docs.anthropic.com/en/docs/agents-and-tools/tool-use/tool-search-tool#custom-tool-search-implementation
 */
export const ToolReferenceBlock = RRecord({
  type: Literal("tool_reference"),
  tool_name: String,
  cache_control: CacheControl.optional(),
});
export type ToolReferenceBlock = Static<typeof ToolReferenceBlock>;

export const ToolResultBlock = RRecord({
  type: Literal("tool_result"),
  tool_use_id: String,
  content: Array(Union(TextBlock, ImageBlock, ToolReferenceBlock)),
  cache_control: CacheControl.optional(),
});
export type ToolResultBlock = Static<typeof ToolResultBlock>;

/**
 * Compaction content block returned by Anthropic server-side compaction.
 */
export const AnthropicCompactionBlock = RRecord({
  type: Literal("compaction"),
  content: String,
  cache_control: CacheControl.optional(),
});
export type AnthropicCompactionBlock = Static<typeof AnthropicCompactionBlock>;

export function isAnthropicCompactionBlock(
  block: unknown,
): block is AnthropicCompactionBlock {
  return AnthropicCompactionBlock.guard(block);
}

/**
 * Streaming delta for Anthropic compaction content.
 */
export const AnthropicCompactionDelta = RRecord({
  type: Literal("compaction_delta"),
  content: String,
});
export type AnthropicCompactionDelta = Static<typeof AnthropicCompactionDelta>;

export function isAnthropicCompactionDelta(
  delta: unknown,
): delta is AnthropicCompactionDelta {
  return AnthropicCompactionDelta.guard(delta);
}

export const AnthropicCompactionEdit = RRecord({
  type: Literal("compact_20260112"),
  trigger: RRecord({
    type: Literal("input_tokens"),
    value: Number,
  }).optional(),
  pause_after_compaction: Boolean.optional(),
  instructions: String.optional(),
});
export type AnthropicCompactionEdit = Static<typeof AnthropicCompactionEdit>;

export const AnthropicContextManagementWithCompaction = RRecord({
  edits: Array(AnthropicCompactionEdit),
});
export type AnthropicContextManagementWithCompaction = Static<
  typeof AnthropicContextManagementWithCompaction
>;

export const AnthropicMessage = RRecord({
  role: AnthropicMessageRoleLiteral,
  content: Array(
    Union(
      TextBlock,
      ToolUseBlock,
      ToolResultBlock,
      AnthropicCompactionBlock,
      ImageBlock,
      DocumentBlock,
      AnthropicThinkingBlock,
      RedactedAnthropicThinkingBlock,
    ),
  ),
});
export type AnthropicMessage = Static<typeof AnthropicMessage>;

export const AnthropicMessages = RRecord({
  systemMessage: String,
  userAndAssistantMessages: Array(AnthropicMessage),
});
export type AnthropicMessages = Static<typeof AnthropicMessages>;

export const AnthropicConformerResult = RRecord({
  messages: AnthropicMessages,
  totalContextTokens: Number.Or(Null),
  usedTokenCountApproximation: Boolean,
});
export type AnthropicConformerResult = Static<typeof AnthropicConformerResult>;

export const AnthropicErrorPayload = RRecord({
  type: Literal("error"),
  error: RRecord({
    type: String,
    message: String,
  }),
});
export type AnthropicErrorPayload = Static<typeof AnthropicErrorPayload>;
